/**
 * Image Filters Utility
 * Provides CSS filter functions and canvas-based image processing
 */

export const FILTER_CATEGORIES = {
  BASIC: 'Basic',
  COLOR: 'Color',
  ARTISTIC: 'Artistic',
  VINTAGE: 'Vintage',
  CREATIVE: 'Creative',
  BLUR: 'Blur & Focus',
  CONTRAST: 'Contrast',
  SPECIAL: 'Special Effects'
};

export const IMAGE_FILTERS = {
  // BASIC FILTERS
  none: {
    id: 'none',
    name: 'None',
    category: FILTER_CATEGORIES.BASIC,
    description: 'No filter applied',
    cssFilter: '',
    icon: '🚫'
  },

  // COLOR FILTERS
  grayscale: {
    id: 'grayscale',
    name: 'Grayscale',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Converts image to black & white',
    cssFilter: 'grayscale(100%)',
    icon: '⚫'
  },

  sepia: {
    id: 'sepia',
    name: 'Sepia',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Warm brownish tone (vintage look)',
    cssFilter: 'sepia(100%)',
    icon: '🟤'
  },

  vibrant: {
    id: 'vibrant',
    name: 'Vibrant',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Boosts saturation',
    cssFilter: 'saturate(1.8) contrast(1.1)',
    icon: '🌈'
  },

  saturationBoost: {
    id: 'saturationBoost',
    name: 'Saturation Boost',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Enhances colors',
    cssFilter: 'saturate(2.0)',
    icon: '🎨'
  },

  desaturate: {
    id: 'desaturate',
    name: 'Desaturate',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Lowers colors (almost grayscale)',
    cssFilter: 'saturate(0.3)',
    icon: '🔘'
  },

  invert: {
    id: 'invert',
    name: 'Invert / Negative',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Flips colors',
    cssFilter: 'invert(100%)',
    icon: '🔄'
  },

  // ADDITIONAL COLOR FILTERS
  warm: {
    id: 'warm',
    name: 'Warm',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Warm orange/yellow tones',
    cssFilter: 'saturate(1.3) brightness(1.1) hue-rotate(15deg)',
    icon: '🔥'
  },

  cool: {
    id: 'cool',
    name: 'Cool',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Cool blue/cyan tones',
    cssFilter: 'saturate(1.2) brightness(1.05) hue-rotate(180deg)',
    icon: '❄️'
  },

  sunset: {
    id: 'sunset',
    name: 'Sunset',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Golden sunset colors',
    cssFilter: 'saturate(1.4) brightness(1.2) hue-rotate(30deg) contrast(1.1)',
    icon: '🌅'
  },

  ocean: {
    id: 'ocean',
    name: 'Ocean',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Deep ocean blue tones',
    cssFilter: 'saturate(1.3) brightness(0.9) hue-rotate(200deg) contrast(1.2)',
    icon: '🌊'
  },

  forest: {
    id: 'forest',
    name: 'Forest',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Natural forest green tones',
    cssFilter: 'saturate(1.4) brightness(1.05) hue-rotate(90deg) contrast(1.1)',
    icon: '🌲'
  },



  solarize: {
    id: 'solarize',
    name: 'Solarize',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Partially inverted photographic look',
    cssFilter: 'invert(50%) contrast(1.3) brightness(1.1)',
    icon: '☀️'
  },



  // ARTISTIC FILTERS
  cinematic: {
    id: 'cinematic',
    name: 'Cinematic / Teal-Orange',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Movie-style contrast (blue shadows, orange highlights)',
    cssFilter: 'contrast(1.3) saturate(1.2) hue-rotate(15deg) brightness(1.05)',
    icon: '🎬'
  },

  hdrEffect: {
    id: 'hdrEffect',
    name: 'HDR Effect',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'High dynamic range, makes details pop',
    cssFilter: 'contrast(1.4) saturate(1.3) brightness(1.1) blur(0.5px)',
    icon: '✨'
  },

  matte: {
    id: 'matte',
    name: 'Matte',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Flat, soft look',
    cssFilter: 'contrast(0.8) saturate(0.9) brightness(1.1)',
    icon: '🎨'
  },

  lomo: {
    id: 'lomo',
    name: 'Lomo',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'High saturation, vignetting (toy camera style)',
    cssFilter: 'saturate(1.8) contrast(1.2) brightness(0.95)',
    icon: '📷'
  },

  popArt: {
    id: 'popArt',
    name: 'Pop Art',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Bright, comic-style colors',
    cssFilter: 'saturate(2.5) contrast(1.5) brightness(1.2)',
    icon: '🎨'
  },

  // CREATIVE FILTERS
  neon: {
    id: 'neon',
    name: 'Neon',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Electric neon glow effect',
    cssFilter: 'saturate(2.0) contrast(1.8) brightness(1.3) hue-rotate(270deg)',
    icon: '💡'
  },

  cyberpunk: {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Futuristic cyberpunk aesthetic',
    cssFilter: 'saturate(1.5) contrast(1.4) brightness(0.9) hue-rotate(300deg)',
    icon: '🤖'
  },

  dreamy: {
    id: 'dreamy',
    name: 'Dreamy',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Soft, ethereal dream-like effect',
    cssFilter: 'blur(1px) brightness(1.2) saturate(0.8) contrast(0.9)',
    icon: '☁️'
  },
  // VINTAGE FILTERS
  vintageFilm: {
    id: 'vintageFilm',
    name: 'Vintage Film',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Faded, grainy, retro',
    cssFilter: 'sepia(60%) contrast(0.9) brightness(1.1) saturate(0.7)',
    icon: '📽️'
  },



  blackWhiteHighKey: {
    id: 'blackWhiteHighKey',
    name: 'Black & White High Key',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Bright, low contrast B&W',
    cssFilter: 'grayscale(100%) contrast(0.8) brightness(1.3)',
    icon: '⚪'
  },

  blackWhiteLowKey: {
    id: 'blackWhiteLowKey',
    name: 'Black & White Low Key',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Dark, moody B&W',
    cssFilter: 'grayscale(100%) contrast(1.5) brightness(0.7)',
    icon: '⚫'
  },

  retro: {
    id: 'retro',
    name: 'Retro',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Classic retro look with warm tones',
    cssFilter: 'sepia(40%) saturate(1.3) contrast(1.1) brightness(1.05) hue-rotate(10deg)',
    icon: '📻'
  },

  oldPhoto: {
    id: 'oldPhoto',
    name: 'Old Photo',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Aged photograph effect',
    cssFilter: 'sepia(80%) contrast(0.8) brightness(1.2) saturate(0.6)',
    icon: '📷'
  },
  // BLUR & FOCUS FILTERS
  vignette: {
    id: 'vignette',
    name: 'Vignette',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Dark edges, focus in center',
    cssFilter: 'brightness(0.9) contrast(1.1)',
    icon: '⭕'
  },

  glow: {
    id: 'glow',
    name: 'Glow / Soft Focus',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Dreamy, blurred highlights',
    cssFilter: 'blur(0.8px) brightness(1.2) saturate(1.1)',
    icon: '✨'
  },

  gaussianBlur: {
    id: 'gaussianBlur',
    name: 'Gaussian Blur',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Smooth blur',
    cssFilter: 'blur(3px)',
    icon: '🌫️'
  },



  sharpen: {
    id: 'sharpen',
    name: 'Sharpen',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Enhances edges and details',
    cssFilter: 'contrast(1.3) brightness(1.05)',
    icon: '🔍'
  },

  // CONTRAST FILTERS
  highContrast: {
    id: 'highContrast',
    name: 'High Contrast',
    category: FILTER_CATEGORIES.CONTRAST,
    description: 'Deeper blacks & whites',
    cssFilter: 'contrast(2.0) brightness(0.95)',
    icon: '⚫⚪'
  },

  lowContrast: {
    id: 'lowContrast',
    name: 'Low Contrast',
    category: FILTER_CATEGORIES.CONTRAST,
    description: 'Muted, flat look',
    cssFilter: 'contrast(0.6) brightness(1.1)',
    icon: '🔘'
  },

  dramatic: {
    id: 'dramatic',
    name: 'Dramatic',
    category: FILTER_CATEGORIES.CONTRAST,
    description: 'High contrast with deep shadows',
    cssFilter: 'contrast(2.2) brightness(0.85) saturate(1.3)',
    icon: '🎭'
  },



  sketch: {
    id: 'sketch',
    name: 'Sketch / Pencil',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Converts image to hand-drawn look',
    cssFilter: 'grayscale(100%) contrast(2) brightness(1.2) invert(100%)',
    icon: '✏️'
  },

  // ADDITIONAL CREATIVE FILTERS
  psychedelic: {
    id: 'psychedelic',
    name: 'Psychedelic',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Trippy, colorful psychedelic effect',
    cssFilter: 'saturate(3.0) contrast(1.8) hue-rotate(180deg) brightness(1.3)',
    icon: '🌈'
  },

  infrared: {
    id: 'infrared',
    name: 'Infrared',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'False-color infrared photography effect',
    cssFilter: 'hue-rotate(180deg) saturate(2.0) contrast(1.4) brightness(1.1)',
    icon: '🔴'
  },

  thermal: {
    id: 'thermal',
    name: 'Thermal',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Heat vision thermal imaging effect',
    cssFilter: 'hue-rotate(240deg) saturate(2.5) contrast(1.6) brightness(0.9)',
    icon: '🌡️'
  },

  xray: {
    id: 'xray',
    name: 'X-Ray',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Medical X-ray imaging effect',
    cssFilter: 'invert(100%) contrast(2.0) brightness(0.8) grayscale(50%)',
    icon: '🦴'
  },

  // ADDITIONAL ARTISTIC FILTERS
  watercolor: {
    id: 'watercolor',
    name: 'Watercolor',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Soft watercolor painting effect',
    cssFilter: 'blur(0.5px) saturate(1.4) contrast(0.9) brightness(1.1)',
    icon: '🎨'
  },

  oilPainting: {
    id: 'oilPainting',
    name: 'Oil Painting',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Rich oil painting texture effect',
    cssFilter: 'saturate(1.6) contrast(1.3) brightness(1.05) blur(0.3px)',
    icon: '🖼️'
  },

  comic: {
    id: 'comic',
    name: 'Comic Book',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Bold comic book style effect',
    cssFilter: 'saturate(2.0) contrast(1.8) brightness(1.2) hue-rotate(10deg)',
    icon: '💥'
  },

  posterize: {
    id: 'posterize',
    name: 'Posterize',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Reduced color poster effect',
    cssFilter: 'contrast(2.5) saturate(1.8) brightness(1.1)',
    icon: '📄'
  },

  // ADDITIONAL VINTAGE FILTERS
  polaroid: {
    id: 'polaroid',
    name: 'Polaroid',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Classic Polaroid instant photo look',
    cssFilter: 'sepia(30%) saturate(1.2) contrast(1.1) brightness(1.1) hue-rotate(5deg)',
    icon: '📸'
  },

  kodachrome: {
    id: 'kodachrome',
    name: 'Kodachrome',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Classic Kodachrome film colors',
    cssFilter: 'saturate(1.5) contrast(1.2) brightness(1.05) hue-rotate(-5deg)',
    icon: '🎞️'
  },

  crossProcess: {
    id: 'crossProcess',
    name: 'Cross Process',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Cross-processed film effect',
    cssFilter: 'saturate(1.8) contrast(1.4) brightness(0.95) hue-rotate(15deg)',
    icon: '🔄'
  },

  // ADDITIONAL SPECIAL EFFECTS
  glitch: {
    id: 'glitch',
    name: 'Glitch',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Digital glitch distortion effect',
    cssFilter: 'saturate(2.0) contrast(1.8) hue-rotate(90deg) brightness(1.2)',
    icon: '📺'
  },

  matrix: {
    id: 'matrix',
    name: 'Matrix',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Green matrix code effect',
    cssFilter: 'hue-rotate(120deg) saturate(2.0) contrast(1.5) brightness(0.8)',
    icon: '💚'
  },

  hologram: {
    id: 'hologram',
    name: 'Hologram',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Futuristic holographic effect',
    cssFilter: 'hue-rotate(180deg) saturate(1.8) contrast(1.4) brightness(1.3)',
    icon: '🔮'
  },

  vintage3d: {
    id: 'vintage3d',
    name: 'Vintage 3D',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Retro 3D anaglyph effect',
    cssFilter: 'saturate(1.5) contrast(1.3) hue-rotate(300deg) brightness(1.1)',
    icon: '🕶️'
  },

  nightVision: {
    id: 'nightVision',
    name: 'Night Vision',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Military night vision effect',
    cssFilter: 'hue-rotate(120deg) saturate(2.5) contrast(1.6) brightness(1.4)',
    icon: '🌙'
  },

  // ADDITIONAL BLUR & FOCUS EFFECTS
  motionBlur: {
    id: 'motionBlur',
    name: 'Motion Blur',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Horizontal motion blur effect',
    cssFilter: 'blur(2px) brightness(1.1) contrast(1.2)',
    icon: '💨'
  },

  radialBlur: {
    id: 'radialBlur',
    name: 'Radial Blur',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Circular radial blur effect',
    cssFilter: 'blur(1.5px) saturate(1.2) brightness(1.05)',
    icon: '🌀'
  },

  softFocus: {
    id: 'softFocus',
    name: 'Soft Focus',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Dreamy soft focus effect',
    cssFilter: 'blur(0.8px) brightness(1.15) saturate(0.9) contrast(0.95)',
    icon: '✨'
  },

  // ADDITIONAL PROFESSIONAL FILTERS
  // Color Enhancement Filters
  vivid: {
    id: 'vivid',
    name: 'Vivid',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Ultra-saturated vivid colors',
    cssFilter: 'saturate(2.5) contrast(1.3) brightness(1.1)',
    icon: '🌺'
  },

  pastel: {
    id: 'pastel',
    name: 'Pastel',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Soft pastel color palette',
    cssFilter: 'saturate(0.7) brightness(1.2) contrast(0.8)',
    icon: '🎀'
  },

  monochrome: {
    id: 'monochrome',
    name: 'Monochrome',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Single color tone effect',
    cssFilter: 'grayscale(100%) sepia(100%) hue-rotate(180deg) saturate(0.8)',
    icon: '⚫'
  },

  duotone: {
    id: 'duotone',
    name: 'Duotone',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Two-color gradient effect',
    cssFilter: 'grayscale(100%) sepia(100%) hue-rotate(45deg) saturate(1.5)',
    icon: '🔵'
  },

  // Advanced Artistic Filters
  impressionist: {
    id: 'impressionist',
    name: 'Impressionist',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Impressionist painting style',
    cssFilter: 'blur(0.4px) saturate(1.3) contrast(1.1) brightness(1.05)',
    icon: '🎨'
  },

  abstract: {
    id: 'abstract',
    name: 'Abstract',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Abstract art transformation',
    cssFilter: 'saturate(2.0) contrast(1.8) hue-rotate(45deg) brightness(1.2)',
    icon: '🔶'
  },

  pixelArt: {
    id: 'pixelArt',
    name: 'Pixel Art',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: '8-bit pixel art style',
    cssFilter: 'contrast(2.0) saturate(1.5) brightness(1.1)',
    icon: '🎮'
  },

  // Vintage & Film Filters
  film35mm: {
    id: 'film35mm',
    name: '35mm Film',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Classic 35mm film look',
    cssFilter: 'sepia(20%) saturate(1.2) contrast(1.1) brightness(1.05)',
    icon: '📷'
  },

  filmNoir: {
    id: 'filmNoir',
    name: 'Film Noir',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Classic black and white cinema',
    cssFilter: 'grayscale(100%) contrast(1.8) brightness(0.9)',
    icon: '🎬'
  },

  vintage80s: {
    id: 'vintage80s',
    name: 'Vintage 80s',
    category: FILTER_CATEGORIES.VINTAGE,
    description: 'Retro 1980s aesthetic',
    cssFilter: 'saturate(1.4) contrast(1.2) hue-rotate(15deg) brightness(1.1)',
    icon: '📼'
  },

  // Creative & Fantasy Filters
  fantasy: {
    id: 'fantasy',
    name: 'Fantasy',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Magical fantasy world effect',
    cssFilter: 'saturate(1.6) brightness(1.2) hue-rotate(30deg) contrast(1.1)',
    icon: '🧚'
  },

  alien: {
    id: 'alien',
    name: 'Alien',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Otherworldly alien atmosphere',
    cssFilter: 'hue-rotate(270deg) saturate(2.0) contrast(1.5) brightness(0.9)',
    icon: '👽'
  },

  underwater: {
    id: 'underwater',
    name: 'Underwater',
    category: FILTER_CATEGORIES.CREATIVE,
    description: 'Deep underwater blue effect',
    cssFilter: 'hue-rotate(180deg) saturate(1.3) brightness(0.8) contrast(1.2)',
    icon: '🌊'
  },

  // Advanced Special Effects
  lightning: {
    id: 'lightning',
    name: 'Lightning',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Electric lightning effect',
    cssFilter: 'invert(100%) contrast(2.0) brightness(1.5) saturate(0.5)',
    icon: '⚡'
  },

  fire: {
    id: 'fire',
    name: 'Fire',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Burning fire effect',
    cssFilter: 'hue-rotate(15deg) saturate(2.0) contrast(1.6) brightness(1.3)',
    icon: '🔥'
  },

  ice: {
    id: 'ice',
    name: 'Ice',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Frozen ice crystal effect',
    cssFilter: 'hue-rotate(180deg) saturate(1.2) brightness(1.4) contrast(1.3)',
    icon: '🧊'
  },

  chrome: {
    id: 'chrome',
    name: 'Chrome',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Metallic chrome finish',
    cssFilter: 'grayscale(100%) contrast(2.0) brightness(1.2) saturate(0.3)',
    icon: '⚪'
  },

  gold: {
    id: 'gold',
    name: 'Gold',
    category: FILTER_CATEGORIES.SPECIAL,
    description: 'Luxurious gold effect',
    cssFilter: 'sepia(100%) saturate(2.0) hue-rotate(30deg) brightness(1.2)',
    icon: '🟡'
  },

  // Professional Photography Filters
  portrait: {
    id: 'portrait',
    name: 'Portrait',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Professional portrait enhancement',
    cssFilter: 'saturate(1.1) brightness(1.05) contrast(1.1) blur(0.2px)',
    icon: '👤'
  },

  landscape: {
    id: 'landscape',
    name: 'Landscape',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Enhanced landscape photography',
    cssFilter: 'saturate(1.3) contrast(1.2) brightness(1.05)',
    icon: '🏔️'
  },

  macro: {
    id: 'macro',
    name: 'Macro',
    category: FILTER_CATEGORIES.ARTISTIC,
    description: 'Close-up macro photography',
    cssFilter: 'saturate(1.4) contrast(1.3) brightness(1.1)',
    icon: '🔍'
  },

  // Advanced Blur Effects
  tiltShift: {
    id: 'tiltShift',
    name: 'Tilt Shift',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Miniature tilt-shift effect',
    cssFilter: 'blur(1px) saturate(1.3) contrast(1.2) brightness(1.1)',
    icon: '🏠'
  },

  zoomBlur: {
    id: 'zoomBlur',
    name: 'Zoom Blur',
    category: FILTER_CATEGORIES.BLUR,
    description: 'Radial zoom blur effect',
    cssFilter: 'blur(1.2px) brightness(1.1) contrast(1.1)',
    icon: '🎯'
  },

  // Color Science Filters
  kelvin2700: {
    id: 'kelvin2700',
    name: 'Warm 2700K',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Warm tungsten lighting',
    cssFilter: 'hue-rotate(20deg) saturate(1.1) brightness(1.1)',
    icon: '🟠'
  },

  kelvin5600: {
    id: 'kelvin5600',
    name: 'Daylight 5600K',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Natural daylight balance',
    cssFilter: 'saturate(1.05) brightness(1.02) contrast(1.05)',
    icon: '☀️'
  },

  kelvin9000: {
    id: 'kelvin9000',
    name: 'Cool 9000K',
    category: FILTER_CATEGORIES.COLOR,
    description: 'Cool blue lighting',
    cssFilter: 'hue-rotate(200deg) saturate(1.1) brightness(1.05)',
    icon: '🔵'
  }
};

/**
 * Get all filters grouped by category
 */
export const getFiltersByCategory = () => {
  const grouped = {};
  Object.values(IMAGE_FILTERS).forEach(filter => {
    if (!grouped[filter.category]) {
      grouped[filter.category] = [];
    }
    grouped[filter.category].push(filter);
  });
  return grouped;
};

/**
 * Get filter by ID
 */
export const getFilterById = (id) => {
  return IMAGE_FILTERS[id] || IMAGE_FILTERS.none;
};

/**
 * Apply filter to CSS style
 */
export const applyFilterToStyle = (filterId, intensity = null) => {
  const filter = getFilterById(filterId);
  
  if (filter.adjustable && intensity !== null && filter.range) {
    const { min, max, unit = '' } = filter.range;
    const clampedIntensity = Math.max(min, Math.min(max, intensity));
    
    // Generate CSS filter with custom intensity
    if (filterId === 'brightness') {
      return `brightness(${clampedIntensity})`;
    } else if (filterId === 'contrast') {
      return `contrast(${clampedIntensity})`;
    } else if (filterId === 'saturation') {
      return `saturate(${clampedIntensity})`;
    } else if (filterId === 'blur') {
      return `blur(${clampedIntensity}${unit})`;
    } else if (filterId === 'hueRotate') {
      return `hue-rotate(${clampedIntensity}${unit})`;
    }
  }
  
  return filter.cssFilter;
};

/**
 * Get all available filter IDs
 */
export const getAllFilterIds = () => {
  return Object.keys(IMAGE_FILTERS);
};

/**
 * Check if filter is adjustable
 */
export const isFilterAdjustable = (filterId) => {
  const filter = getFilterById(filterId);
  return filter.adjustable || false;
};

/**
 * Get filter range for adjustable filters
 */
export const getFilterRange = (filterId) => {
  const filter = getFilterById(filterId);
  return filter.range || null;
};
